/**
 * Hook for reading/writing ONE key of an entity's dictionary (S5).
 *
 * A node's data lives in
 * `entities.properties`, with one writer per node, so a curated edit is a
 * top-level MERGE of the single key it touches — never a wholesale replace,
 * which belongs to the sync path that owns the whole dictionary.
 */

import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";

interface EntityResponse {
  readonly id: string;
  readonly properties?: Record<string, unknown> | null;
}

interface UseEntityPropertyResult {
  readonly value: string;
  readonly isLoading: boolean;
  readonly save: (value: string) => void;
}

const SAVE_DEBOUNCE_MS = 800;

export function useEntityProperty(
  entityId: string | undefined,
  key: string,
): UseEntityPropertyResult {
  const runtime = useAppRuntime();
  const queryClient = useQueryClient();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryKey = ["entity-properties", entityId];

  const { data: entity, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      runtime.transport.rpc<EntityResponse>("graph.entity.get", { id: entityId }),
    enabled: !!entityId,
  });

  const raw = entity?.properties?.[key];
  const value = typeof raw === "string" ? raw : "";

  const save = useCallback(
    (next: string) => {
      if (!entityId) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      saveTimerRef.current = setTimeout(async () => {
        try {
          await runtime.transport.rpc("graph.entity.update_properties", {
            entity_id: entityId,
            properties: { [key]: next },
          });
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          queryClient.invalidateQueries({ queryKey });
        } catch (err) {
          console.error("Failed to save property:", err);
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [entityId, key, runtime.transport, queryClient, queryKey],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return { value, isLoading, save };
}

/** The whole dictionary of an entity, read-only (S5). One crossing, cached
 *  with `useEntityProperty` under the same key so an edit refreshes both. */
export function useEntityProperties(entityId: string | undefined): {
  readonly properties: Record<string, unknown>;
  readonly isLoading: boolean;
} {
  const runtime = useAppRuntime();
  const { data: entity, isLoading } = useQuery({
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    queryKey: ["entity-properties", entityId],
    queryFn: () =>
      runtime.transport.rpc<EntityResponse>("graph.entity.get", { id: entityId }),
    enabled: !!entityId,
  });
  return { properties: entity?.properties ?? {}, isLoading };
}
